import React from "react";
import { useTranslation } from "react-i18next";
import {
  DealQuestionnaireQuestionSettings,
  QuestionResponse,
  Responses,
  ResponseValue,
  Score
} from "@/pages/deals/types";
import Text, { TextVariant } from "@/components/ui/text";
import { InfoCircle } from "@dreamhub/designsystem/icons";
import { cn } from "@/utils/utils";
import { useUpdateDealQuestionnaireMutation } from "@/pages/deals/hooks";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { PersonData } from "@/entities/people/types";
import { getFullName } from "@/entities/people/helpers";
import MandatoryIndication from "@/components/controls/Questions/MandatoryIndication";
import DreamerIndication from "@/components/controls/Questions/DreamerIndication/DreamerIndication";
import IconContainer from "@/components/ui/icon-container";
import Badge from "@/components/ui/badge";
import {
  htmlToMarkdown,
  isHtmlContent
} from "@/components/controls/TextEditor/utils/markdownConverter";

interface BaseQuestionProps {
  question: DealQuestionnaireQuestionSettings;
  questionnaireId: string;
  dealId: string;
  handleUpdateQuestionnaireScore: (newValue: Score | null) => void;
  localResponses: Responses;
  updateResponse: (
    questionId: string,
    response: QuestionResponse | null
  ) => void;
  onResponseError: () => void;
  className?: string;
  hideMandatorySign?: boolean;
  hideTooltip?: boolean;
  relatedQuestion?: DealQuestionnaireQuestionSettings & {
    person: PersonData | null;
  };
  children?: React.ReactNode;
  questionContainerClassName?: string;
  questionClassName?: string;
  questionTextClassName?: string;
  questionVariant?: TextVariant;
  isValueDisplayable?: boolean;
}

const BaseQuestion: React.FC<BaseQuestionProps> = ({
  question,
  questionnaireId,
  dealId,
  handleUpdateQuestionnaireScore,
  localResponses,
  updateResponse,
  onResponseError,
  className,
  hideMandatorySign = false,
  hideTooltip = false,
  relatedQuestion,
  children,
  questionContainerClassName,
  questionClassName,
  questionTextClassName,
  questionVariant,
  isValueDisplayable
}) => {
  const { t } = useTranslation();
  const updateQuestionnaire = useUpdateDealQuestionnaireMutation();
  const { id, questionText, isMandatory } = question;
  const responsePerson = localResponses[id]?.person;
  const handleUpdateResponse = (value: any) => {
    const existingResponse =
      localResponses[id] || ({} as QuestionResponse | null);

    const normalizeValue = (val: any): string => {
      if (typeof val !== "string" || !val) return val;
      return isHtmlContent(val) ? htmlToMarkdown(val) : val;
    };

    const normalizedNewValue = normalizeValue(value);
    const normalizedExistingValue = normalizeValue(
      existingResponse?.response?.value
    );

    if (normalizedNewValue === normalizedExistingValue) return;

    const newResponse = {
      ...existingResponse,
      response: { ...existingResponse?.response, value },
      lastEditedBy: undefined
    };

    updateResponse(id, newResponse);

    updateQuestionnaire.mutate(
      {
        pathParams: { dealId, questionnaireId },
        data: {
          questions: [
            {
              questionId: id,
              ...newResponse
            }
          ]
        }
      },
      {
        onSuccess: (data) => {
          handleUpdateQuestionnaireScore(data.score);
        },
        onError: onResponseError
      }
    );
  };

  const renderQuestionText = () => {
    const personToUse = relatedQuestion?.person || responsePerson;
    return personToUse ? getFullName(personToUse) : questionText;
  };

  const renderJobTitle = () => {
    const jobTitle =
      relatedQuestion?.person?.jobTitle || responsePerson?.jobTitle;
    return jobTitle ? (
      <Text variant="smBodySecondary" className="self-end pb-[1px]">
        {jobTitle}
      </Text>
    ) : null;
  };

  return (
    <div key={id} className={cn("flex flex-col gap-2", className)}>
      <div className={cn("flex items-center gap-2 mx-1.5", questionClassName)}>
        <Text
          variant={questionVariant || "bodySecondary"}
          className={questionTextClassName}
        >
          {renderQuestionText()}
        </Text>
        {renderJobTitle()}
        {relatedQuestion?.person && (
          <Badge
            text={t("deals.dealView.mappedAs", { position: questionText })}
            textVariant="smBodySecondary"
            className="p-1"
          />
        )}
        {isMandatory && !hideMandatorySign && <MandatoryIndication />}
        {question.tooltipText && !hideTooltip && (
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="ml-1">
                <IconContainer size="sm" color="secondary">
                  <InfoCircle />
                </IconContainer>
              </div>
            </TooltipTrigger>
            <TooltipContent maxWidth={13}>
              <Text variant="smBody">{question.tooltipText}</Text>
            </TooltipContent>
          </Tooltip>
        )}
        <DreamerIndication
          dealId={dealId}
          questionnaireId={questionnaireId}
          questionId={id}
          questionResponse={localResponses[id]}
          isValueDisplayable={isValueDisplayable}
        />
      </div>
      <div className={cn("pl-1", questionContainerClassName)}>
        {React.isValidElement(children)
          ? React.cloneElement(children as React.ReactElement, {
              value: (localResponses[id]?.response as ResponseValue)?.value,
              onChange: handleUpdateResponse,
              question: question
            })
          : children}
      </div>
    </div>
  );
};

export default BaseQuestion;
